import { AddLayerObject, LayerSpecification } from 'maplibre-gl';
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Spinner from '@/components/common/loading/spinner';
import ImageDisplayBorder from '@/components/insights/imageDisplayBorder';
import Typography from '@/components/typography';
import getLatestCDLImage from '@/endpoints/schoolcraft/cdl/getLatestCDLImage';
import getLatestCDLStats from '@/endpoints/schoolcraft/cdl/getLatestCDLStats';
import useImageDisplayTracking from '@/hooks/useImageDisplay';
import { useTileConfig } from '@/hooks/useTileConfig';
import { AcresMapboxLayerIds } from '@/mapbox-layers';
import {
  ICDLStatsResponse,
  ICDLVectorResponse,
  ILatestCDLImageResponse,
  ILatestCDLStatsResponse,
  ILayer,
} from '@/models/cdl';
import { AcresFeature } from '@/models/features';
import {
  GetGeoServerQuery,
  useGetCDLImageQuery,
  useGetCDLStatsQuery,
} from '@/redux/api/geoserverApi';
import { round } from '@/utils/common';
import Logger from '@/utils/logger';
import { multiUnion } from '@/utils/multiUnion';

import Legend from './legend';

export function makeCDLContextLayer(
  url: string,
  year: string | number
): AddLayerObject {
  return {
    id: `${AcresMapboxLayerIds.COG_CDL_CONTEXT_BASE}${year}`,
    paint: {
      'raster-opacity': 0.5,
      'raster-resampling': 'nearest',
    },
    source: {
      maxzoom: 12,
      minzoom: 12,
      scheme: 'tms',
      tiles: [`${url}{z}/{x}/{y}`],
      type: 'raster',
    },
    type: 'raster',
  };
}

export const transformCDLStats = (
  stats: ICDLStatsResponse
): ICDLStatsResponse => {
  const { data, labels } = stats;
  const newData = [
    ...data.slice(0, data.length - 1).reverse(),
    data[data.length - 1],
  ];
  const newLabels = [
    ...labels.slice(0, labels.length - 1).reverse(),
    labels[labels.length - 1],
  ];
  const newStats = {
    ...stats,
    data: newData,
    labels: newLabels,
  };

  return newStats;
};

interface IProps {
  year: string;
  allFeatures: AcresFeature[];
  active: boolean;
  toggleLayer: (_data: ICDLVectorResponse) => void;
  handleClick?: () => void;
  isFresh: boolean;
  showLatestYear?: boolean;
}

const ImageDisplay: FunctionComponent<IProps> = ({
  year,
  allFeatures,
  active,
  toggleLayer,
  handleClick = undefined,
  isFresh,
  showLatestYear = false,
}) => {
  const payload: GetGeoServerQuery = useMemo(
    () => ({
      boundary: multiUnion(allFeatures),
      year,
    }),
    [allFeatures, year]
  );

  const [latestYearImg, setLatestYearImg] = useState<
    ILatestCDLImageResponse | undefined
  >(undefined);

  const [latestYearStats, setLatestYearStats] = useState<
    ILatestCDLStatsResponse | undefined
  >(undefined);

  const { tileConfig } = useTileConfig();

  const { data: cdlStats, isFetching: areStatsLoading } = useGetCDLStatsQuery(
    payload,
    {
      skip: showLatestYear,
    }
  );
  const {
    data: cdlData,
    isFetching: isLegendLoading,
    isSuccess: isLegendSuccessful,
    isError: isLegendFailure,
  } = useGetCDLImageQuery(payload, {
    skip: showLatestYear,
  });

  /** The map context layer is the layer in the "background" that has an opacity of .5 showing surrounding crop history */
  const mapContextLayer: ICDLVectorResponse = useMemo(() => {
    const yearUrls = tileConfig?.['crop-history'];
    if (!yearUrls) return { layers: [] };

    const url = yearUrls[year.toString()];

    const layer = makeCDLContextLayer(url, year);

    return { layers: [layer] };
  }, [tileConfig, year]);

  const fetchLatestYearStats = useCallback(async () => {
    if (!allFeatures || !showLatestYear) return;
    try {
      const { res, err } = await getLatestCDLStats({
        boundary: multiUnion(allFeatures) as AcresFeature,
      });

      if (err) throw Error(err.message);

      if (res) {
        setLatestYearStats(res.data);
      }
    } catch (err) {
      Logger.error(err);
    }
  }, [allFeatures, showLatestYear]);

  const fetchLatestYearImg = useCallback(async () => {
    if (!allFeatures || !showLatestYear) return;
    try {
      const { res, err } = await getLatestCDLImage({
        boundary: multiUnion(allFeatures) as AcresFeature,
      });

      if (err) throw Error(err.message);

      if (res?.data) {
        setLatestYearImg(res.data);
      }
    } catch (err) {
      Logger.error(err);
    }
  }, [allFeatures, showLatestYear]);

  useEffect(() => {
    fetchLatestYearImg();
    fetchLatestYearStats();
  }, [fetchLatestYearImg, fetchLatestYearStats]);

  /** This layer is the 100% opacity main image that shows on the selected land */
  const cdlMapLayer = useMemo(
    () =>
      cdlData?.cdlMapLayer ||
      (latestYearImg
        ? { layers: [latestYearImg.info as unknown as LayerSpecification] }
        : { layers: [] }),
    [cdlData?.cdlMapLayer, latestYearImg]
  );
  const cdlImage = useMemo(
    () => (cdlData?.cdlImageData || latestYearImg?.info) as ILayer,
    [cdlData, latestYearImg?.info]
  );
  const cdlStatsMemoized = useMemo(() => {
    return cdlStats || latestYearStats?.info;
  }, [cdlStats, latestYearStats?.info]);

  useImageDisplayTracking(year, {
    isError: isLegendFailure,
    isLoading: isLegendLoading,
    isSuccess: isLegendSuccessful,
  });

  useEffect(() => {
    if (!mapContextLayer) return;
    if (!cdlMapLayer) return;

    const combinedLayers: ICDLVectorResponse = {
      layers: [...mapContextLayer.layers, ...cdlMapLayer.layers],
    };

    if (active) {
      toggleLayer(combinedLayers);
    }
  }, [active, cdlMapLayer, mapContextLayer, toggleLayer]);

  const renderStats = () => {
    if (areStatsLoading || !isFresh) {
      return (
        <div className="flex w-full flex-col flex-wrap items-center justify-center">
          <Typography className="!w-4/5" />
          <Typography className="!w-4/5" />
          <Typography className="!w-4/5" />
        </div>
      );
    }

    if (!cdlStats && !cdlStatsMemoized) return null;

    return (
      <div className="flex flex-1 flex-col pl-4">
        <Typography className="mb-2 text-left text-gray-70" variant="h3">
          {year}
        </Typography>
        {cdlStatsMemoized?.labels.map((label, i) => (
          <div
            key={label}
            className="flex h-full w-full items-center justify-between text-gray-70"
          >
            <div className="flex items-center">
              <Legend
                legend={cdlStatsMemoized.legend.find(
                  (item) => item.name === label
                )}
              />
              <Typography className="w-20 truncate text-left" title={label}>
                {label}
              </Typography>
            </div>
            <div>
              <Typography className="ml-1" variant="caption">{`${round(
                cdlStatsMemoized.data[i] * 100,
                1
              )}%`}</Typography>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderImage = () => {
    if (isLegendLoading || !isFresh) {
      return (
        <div className="flex h-full w-full items-center justify-center rounded">
          <Spinner />
        </div>
      );
    }

    if (cdlImage) {
      return (
        <img
          alt="crop history source"
          className="image-rendering-pixelated h-auto max-h-[120px] w-full max-w-sm rounded object-contain"
          src={cdlImage.source.url}
        />
      );
    }

    return null;
  };

  return (
    <ImageDisplayBorder handleClick={handleClick} isActive={active}>
      {cdlStatsMemoized && (
        <div className="flex w-full items-center">
          <div className="flex h-full w-2/5 items-center">{renderImage()}</div>
          <div className="w-3/5">{renderStats()}</div>
        </div>
      )}
    </ImageDisplayBorder>
  );
};

export default memo(ImageDisplay);
